// client/src/shared/lib/build-version-guard.ts
// Garde de version du build client (backlog #10597, priorite 0 dogfood Pulsar).
//
// Probleme : quand un nouveau build client est deploye, rien ne le signale a un onglet deja
// ouvert. Fabrice continue a utiliser l'ancien bundle en memoire sans le savoir tant qu'il ne
// recharge pas la page a la main (nature d'une SPA : le tag <script> ne se re-execute jamais
// tout seul).
//
// Detection SANS AUCUNE dependance serveur : Vite grave un nom de bundle hashe (qui change a
// chaque build) dans index.html. Ce tag <script type="module" src="/app/assets/index-HASH.js">
// reste present, inchange, dans le DOM vivant de l'onglet pendant toute sa duree de vie. On
// compare ce src de reference a un refetch de /app/index.html (deja servi en statique par nginx,
// zero cout serveur, zero authentification, zero nouvelle route).
//
// Fail-safe par construction (non negociable, cf relecture Claude) : toute incapacite a verifier
// (reseau coupe, reponse non-ok, corps vide/illisible, pas de bundle de reference disponible)
// fait retourner `false` -- silence, jamais une alerte fondee sur une supposition. Un mecanisme
// qui crie au loup pendant une simple coupure reseau serait pire que l'absence de mecanisme.
//
// Jamais de rechargement automatique : ce module ne fait que SIGNALER (store `updateAvailable`).
// La decision de recharger reste entierement a Fabrice (bouton dans le bandeau, ChatPage.tsx).

import { create } from 'zustand'
import { dbgEvent } from './debug'

const INDEX_URL = '/app/index.html'

/**
 * Lit dans le DOM vivant le src du bundle JS actuellement execute par cet onglet. Zero requete :
 * c'est deja present depuis le chargement de la page (Vite l'a ecrit dans index.html au build).
 * Le parametre `doc` est injectable pour rendre la fonction testable sans navigateur reel (le
 * test fournit un faux objet avec juste `querySelector`).
 * Retourne null si aucun script module n'est trouve (ex. mode dev Vite, qui sert /src/main.tsx
 * sans hash) -- dans ce cas le garde reste inerte, jamais de faux positif faute de reference.
 */
export function captureCurrentBundleSrc(doc: Pick<Document, 'querySelector'> = document): string | null {
  const script = doc.querySelector('script[type="module"]')
  return script?.getAttribute('src') ?? null
}

/**
 * Compare le bundle courant a celui reellement servi maintenant par le serveur, en refetchant
 * index.html avec cache:'no-store' (aucun Cache-Control n'est pose sur cette route -- s'appuyer
 * sur la mise en cache heuristique du navigateur serait fragile, cf miniplan #1.5).
 *
 * Retourne true UNIQUEMENT si la comparaison a pu etre faite ET que le bundle a change. Toute
 * incapacite a verifier retourne false : jamais d'exception propagee, jamais une alerte fondee
 * sur un echec de verification.
 *
 * `fetchFn` est injectable pour rendre la fonction testable sans reseau reel (le test fournit un
 * faux fetch controle -- succes/echec/reponse vide -- sans jamais toucher un serveur ou un
 * daemon).
 */
export async function checkForNewBuild(
  currentSrc: string | null,
  fetchFn: typeof fetch = fetch,
): Promise<boolean> {
  if (!currentSrc) return false
  try {
    const res = await fetchFn(INDEX_URL, { cache: 'no-store' })
    if (!res.ok) return false
    const html = await res.text()
    if (!html) return false
    return !html.includes(currentSrc)
  } catch {
    return false
  }
}

// ─── Store ────────────────────────────────────────────────────────────────
// Surface volontairement petite (un src de reference + deux booleens) : fusionne ici plutot que
// dans un fichier store.ts dedie (decision relecture Claude -- 3 fichiers valent mieux que 4 si
// le 4e ne porte rien).

interface BuildVersionState {
  /** src du bundle capture au demarrage de l'onglet -- reference de comparaison. */
  baselineSrc: string | null
  /** true des qu'un fetch de controle a detecte un bundle different de baselineSrc. Monotone :
   *  ne repasse jamais a false pour cet onglet (un reload repart d'un etat frais). */
  updateAvailable: boolean
  /** true si Fabrice a ferme le bandeau. Remis a false au prochain retour reel sur l'onglet
   *  (focus/visibilitychange) -- fermer veut dire "pas maintenant", jamais "ne me le redis plus". */
  dismissed: boolean

  /** A appeler une fois, au montage de l'app : fige la reference de comparaison. */
  init: () => void
  /** Relance la comparaison serveur. Sans effet si aucune reference n'a ete capturee (mode dev). */
  check: () => Promise<void>
  /** Ferme le bandeau pour l'instant (n'efface pas updateAvailable). */
  dismiss: () => void
  /** Remet le bandeau en jeu -- a appeler sur un retour reel sur l'onglet, pas sur le filet 5min. */
  clearDismissed: () => void
}

export const useBuildVersionStore = create<BuildVersionState>((set, get) => ({
  baselineSrc: null,
  updateAvailable: false,
  dismissed: false,

  init: () => {
    const baselineSrc = captureCurrentBundleSrc()
    dbgEvent('Zustand', 'buildVersion.init', { baselineSrc })
    set({ baselineSrc })
  },

  check: async () => {
    const { baselineSrc, updateAvailable } = get()
    const found = await checkForNewBuild(baselineSrc)
    if (found && !updateAvailable) {
      dbgEvent('Zustand', 'buildVersion.check -> nouvelle version detectee', { baselineSrc })
      set({ updateAvailable: true })
    }
  },

  dismiss: () => {
    dbgEvent('Zustand', 'buildVersion.dismiss')
    set({ dismissed: true })
  },

  clearDismissed: () => {
    set({ dismissed: false })
  },
}))
